/**
 * src/pages/Pricing.jsx
 * Tabla de precios pública — accesible sin login.
 *
 * Los alcances de cada plan replican la tabla v2 cerrada el 17-07-2026 y viven
 * en src/data/planes.js, compartidos con el alta; la fuente de verdad del
 * enforcement sigue en app/services/tier_limits.py, que NO guarda precios
 * (editar ambos a la vez si cambian los topes).
 *
 * ⚠️ Enterprise YA NO SE PINTA AQUÍ, y de ahí viene la limpieza del 30-10-2026.
 * La tabla v3 lo sacó del escaparate dejándolo fuera de `PLANES` (su ficha vive en
 * `PLAN_A_MEDIDA`), y esta rejilla recorre `PLANES`: todo lo que este fichero tenía
 * para tratarlo —el mailto comercial, el «Precio a medida», el «Hablar con ventas» y
 * el prefijo «desde»— quedó **inalcanzable**. Se retira, porque una rama muerta que
 * mira a un plan retirado es la que alguien reactiva sin querer.
 *
 * 🔑 Pero Enterprise NO está muerto en las públicas: `buscarPlan()` sigue
 * encontrándolo y `Register.jsx` y `Acceder.jsx` lo renderizan si llega por la URL
 * (`/register?tier=enterprise` pinta su ficha, medido), que es el camino de la venta
 * asistida. Lo que se borra es el de ESTA pantalla, que no tiene botón del que
 * colgarlo.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { crearCheckout } from '../api/billing'
// La tabla ya no vive aquí: el alta necesita el MISMO precio y una segunda copia
// habría divergido el día que alguien tocara una (ERRORES_RECURRENTES §4).
import {
  DIAS_TRIAL, MESES_ANUAL, PLANES, fmtUF as fmt,
  PIONERO_DESCUENTO_PCT, PIONERO_MESES_DESCUENTO, PIONERO_SOLO_ANUAL,
} from '../data/planes'

/** Los meses que el ciclo anual regala: se pagan `MESES_ANUAL` y se usan 12. */
const MESES_GRATIS = 12 - MESES_ANUAL

export default function Pricing() {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState('')
  const [anual, setAnual] = useState(false)

  async function handlePlan(tier) {
    setError('')
    const ciclo = anual ? 'anual' : 'mensual'
    if (!isAuthenticated) {
      navigate(`/register?tier=${tier}&ciclo=${ciclo}`)
      return
    }
    setLoading(tier)
    try {
      const { url } = await crearCheckout(tier, ciclo)
      window.location.href = url
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al iniciar el pago')
    } finally { setLoading(null) }
  }

  const btnCiclo = (activo) => ({
    padding: '7px 18px', borderRadius: 'var(--radius-pill)', border: 'none', cursor: 'pointer',
    fontFamily: 'var(--font-ui)', fontSize: 'var(--text-base)', fontWeight: 700,
    background: activo ? 'var(--color-surface)' : 'transparent',
    color: activo ? 'var(--color-text)' : 'var(--color-text-2)',
    boxShadow: activo ? '0 1px 3px rgba(30,35,42,.10)' : 'none',
  })

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', padding: '60px 24px' }}>
      <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '38px' }}>
          <div style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)', color: 'var(--color-accent)', letterSpacing: '0.1em', marginBottom: 'var(--space-3)' }}>PRECIOS</div>
          <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 'var(--space-3)' }}>Simple y transparente</h1>
          {/* ⚠️ Decía «Precios especiales de lanzamiento» y se fue por la regla 2
              del p9 §2, que SIGUE viva con la tabla v3: el precio es público y no
              se abre con una concesión. Lo que ya no sostiene esa retirada es el
              porqué que se escribió aquí el 26-08 —«la tarifa es el precio de
              lista congelado, sin rebaja»—, porque desde el 28-10-2026 el
              programa sí lleva un descuento. Va en el bloque del Pionero, con su
              duración al lado, y no aquí arriba de gancho. */}
          <p style={{ color: 'var(--color-text-2)', fontSize: '16px' }}>{DIAS_TRIAL} días de prueba gratis · sin permanencia</p>

          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', marginTop: 'var(--space-5)', background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-pill)', padding: 'var(--space-1)' }}>
            <button onClick={() => setAnual(false)} style={btnCiclo(!anual)}>Mensual</button>
            <button onClick={() => setAnual(true)} style={btnCiclo(anual)}>Anual</button>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10.5px', color: 'var(--color-ok)', padding: '0 10px 0 6px', whiteSpace: 'nowrap' }}>
              {/* 🔴 Aquí decía «2 meses gratis» A MANO, y el 29-10-2026 dejó de ser
                  verdad: la tabla v3 subió MESES_ANUAL de 10 a 11, o sea de dos meses
                  regalados a uno. El build pasaba, ninguna prueba se ponía roja y esta
                  página pública prometía el DOBLE de lo que el producto da. Se calcula
                  del mismo sitio que el precio para que no pueda volver a pasar. */}
              {MESES_GRATIS} {MESES_GRATIS === 1 ? 'mes' : 'meses'} gratis
            </span>
          </div>
        </div>

        {error && <div style={{ textAlign: 'center', color: 'var(--color-error)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-base)', marginBottom: '20px' }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px', alignItems: 'stretch' }}>
          {PLANES.map(plan => {
            const mensual = plan.precio
            const porMes = anual ? (mensual * MESES_ANUAL) / 12 : mensual
            const totalAnual = mensual * MESES_ANUAL
            return (
              <div key={plan.id} style={{
                background: 'var(--color-surface)',
                border: `1px solid ${plan.featured ? 'var(--color-accent)' : 'var(--color-border)'}`,
                borderRadius: '14px', padding: '28px 24px',
                display: 'flex', flexDirection: 'column',
                position: 'relative',
              }}>
                {plan.featured && (
                  <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--color-accent)', color: 'var(--color-on-accent)', fontSize: 'var(--text-xs)', fontWeight: 600, padding: '3px 14px', borderRadius: 'var(--radius-pill)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                    Más elegido
                  </div>
                )}
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 'var(--space-2)' }}>{plan.nombre}</div>

                <div style={{ fontSize: '32px', fontWeight: 700, fontFamily: 'var(--font-mono)', letterSpacing: '-0.03em', marginBottom: 'var(--space-05)' }}>
                  {fmt(porMes)}<span style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-3)', fontWeight: 400 }}> UF/mes</span>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11.5px', color: 'var(--color-text-3)', minHeight: '18px' }}>
                  {anual ? `${fmt(totalAnual)} UF/año · facturación anual` : 'Facturación mensual'}
                </div>

                <p style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-2)', margin: '12px 0 16px' }}>{plan.desc}</p>
                <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '0 0 16px' }} />

                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', flex: 1, marginBottom: '22px', padding: 0 }}>
                  {plan.heredaDe && (
                    <li style={{ fontSize: 'var(--text-base)', fontWeight: 700, color: 'var(--color-text)', marginBottom: 'var(--space-05)' }}>
                      Todo lo de {plan.heredaDe}, y además:
                    </li>
                  )}
                  {plan.features.map(f => (
                    <li key={f} style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-2)', display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
                      <span style={{ color: 'var(--color-accent)', fontSize: 'var(--text-sm)', flexShrink: 0, marginTop: 'var(--space-05)' }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePlan(plan.id)}
                  disabled={loading === plan.id}
                  style={{
                    width: '100%', padding: '11px', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-md)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)',
                    background: plan.featured ? 'var(--color-accent)' : 'var(--color-surface-2)',
                    color: plan.featured ? 'var(--color-on-accent)' : 'var(--color-text)',
                    border: plan.featured ? 'none' : '1px solid var(--color-border)',
                    opacity: loading === plan.id ? 0.7 : 1,
                  }}
                >
                  {loading === plan.id ? 'Redirigiendo...' : 'Empezar prueba gratis'}
                </button>
              </div>
            )
          })}
        </div>

        <p style={{ textAlign: 'center', marginTop: '26px', fontSize: 'var(--text-base)', color: 'var(--color-text-2)' }}>
          Durante los <strong>{DIAS_TRIAL} días de prueba</strong> se desbloquean <strong>Módulo Competencia</strong> y <strong>Predictor</strong> en cualquier plan.
        </p>
        <ProgramaPionero />

        <p style={{ textAlign: 'center', marginTop: '10px', fontSize: 'var(--text-base)', color: 'var(--color-text-3)' }}>
          Precios en UF · IVA no incluido · Se factura en pesos al valor de la UF del día y se paga por transferencia · Cancela en cualquier momento
        </p>
      </div>
    </div>
  )
}

/**
 * El Programa Pionero — la única ventaja real de esta fase, y hasta hoy no
 * estaba escrita en NINGUNA pantalla del producto.
 *
 * Se ciñe a las cuatro reglas de encuadre del p9 §2, que gobiernan todo lo que
 * se diga del programa (landing, precios, correos y conversaciones):
 *
 *   R1 · Se dice que es temprano, no se disimula. Se descubre igual, y quien lo
 *        descubra tras un relato inflado se siente engañado — además el producto
 *        que se vende es «te digo la verdad sobre tus datos».
 *   R2 · El precio es público. Lo que no se hace es ABRIR con el descuento: no
 *        es el gancho, no caduca en un contador y no hay «precio especial de
 *        lanzamiento». Esta regla sigue entera con la tabla v3.
 *   R3 · ⚠️ REFORMADA por Manuel el 28-10-2026, y hay que leerla por mitades.
 *        Decía «Selección, no rebaja», y desde la tabla v3 el programa SÍ lleva
 *        una rebaja —PIONERO_DESCUENTO_PCT durante PIONERO_MESES_DESCUENTO
 *        meses, solo en anual—, así que la mitad del «no rebaja» está muerta: la
 *        ventaja de abajo la nombra, con la redacción que aprobó Manuel el 30-10.
 *        Sobrevive la otra mitad, que es la que gobierna esta sección: el titular
 *        es el programa y no el precio, y por eso el descuento va de ventaja con
 *        su duración al lado, nunca en el h2 ni en la rejilla de planes de arriba.
 *        🔴 Lo que NO se ha reescrito es el argumento, y no es de ningún hilo:
 *        el «por qué 24 y no 12», el «doble golpe del mes 24» y la regla 3 tal
 *        como sigue impresa en el §2 del p9 defienden la forma anterior. Es
 *        texto comercial de Manuel y le queda pendiente en el §3.1. Las cifras
 *        se mueven sin él; el encuadre, no.
 * ⚠️ El programa se llamaba «Fundador» hasta el 26-08. Se cambió porque en
 * Chile «socio fundador» significa participación societaria, y en un contrato
 * B2B donde el cliente es una empresa, «entra como fundadora» admite esa
 * lectura sin forzarla. «Pionera» describe a quien adopta primero, nunca a
 * quien posee. «Piloto» y «acceso anticipado» se descartaron por lo contrario:
 * insinúan que el cliente está probando, y aquí paga la tarifa completa.
 *
 *   R4 · ⚠️ SIN contador público. Ni «quedan 6 de 10» ni «los 10 primeros de
 *        este mes»: lo segundo afirma un flujo de diez clientes al mes que no
 *        existe, y en el mes 2 es visiblemente falso. Cuántas plazas quedan se
 *        dice en la conversación 1-a-1. Si alguien añade aquí un contador, está
 *        rompiendo la regla — hay un test que lo impide.
 *
 * Y el intercambio va escrito (p9 §4): una pionera que no da nada a cambio es
 * un cliente con descuento, que es justo lo que el programa NO es.
 */
function ProgramaPionero() {
  return (
    <section style={{
      marginTop: 'var(--space-7)',
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-6)',
      maxWidth: '860px',
      marginInline: 'auto',
    }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: 'var(--color-accent)', marginBottom: 'var(--space-3)',
      }}>
        Programa Pionero
      </div>

      <h2 style={{
        fontSize: 'var(--text-xl)', fontWeight: 700, margin: 0,
        letterSpacing: '-0.01em', color: 'var(--color-text)',
      }}>
        Diez empresas entran como pioneras
      </h2>

      <p style={{
        color: 'var(--color-text-2)', fontSize: 'var(--text-md)',
        marginTop: 'var(--space-3)', marginBottom: 0, lineHeight: 1.6,
      }}>
        CM Monitor es una herramienta joven y no lo disimulamos: operación pequeña,
        producto en construcción. A cambio de aceptar eso y de dedicarle algo de
        tiempo, las diez primeras empresas entran en condiciones que después no
        se repiten.
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
        gap: 'var(--space-5)', marginTop: 'var(--space-5)',
      }}>
        {/* 🔴 Aquí decía «El precio de esta tabla, congelado 24 meses» y «No es un
            descuento», y con la tabla v3 (Manuel, 28-10-2026) dice lo contrario de lo
            que hay: el Pionero SÍ es un descuento, del 25 % durante 12 meses y solo en
            anual. Redacción nueva aprobada por Manuel el 30-10-2026. Las tres cifras se
            leen de `planes.js` y no se escriben, por lo mismo que el rótulo del anual:
            esta página ya prometió una vez el doble de lo que el producto daba. */}
        <Ventaja titulo={`Un ${PIONERO_DESCUENTO_PCT} % menos durante ${PIONERO_MESES_DESCUENTO} meses${PIONERO_SOLO_ANUAL ? ', en el plan anual' : ''}`}>
          Al vencer se paga la tarifa de lista, sin sorpresas: no se acaba ninguna
          oferta — se acaba el descuento, que es lo que se ofreció.
        </Ventaja>
        <Ventaja titulo="Acceso directo a quien la construye">
          Sin ticket ni cola. Respuesta del responsable.
        </Ventaja>
        <Ventaja titulo="Voz en qué se construye antes">
          Una herramienta madura no puede ofrecer esto. Esta sí, y es la ventaja
          que de verdad caduca.
        </Ventaja>
      </div>

      <div style={{
        marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)',
        borderTop: '1px solid var(--color-border)',
        color: 'var(--color-text-2)', fontSize: 'var(--text-base)', lineHeight: 1.6,
      }}>
        <strong style={{ color: 'var(--color-text)' }}>Qué pedimos a cambio.</strong>{' '}
        Una llamada de 30 minutos al mes durante los tres primeros meses —tres en
        total— y permiso para nombrarte como caso de éxito si quedas satisfecho,
        <strong> revocable en cualquier momento y sin condiciones</strong>: si un
        día no quieres aparecer, dejas de aparecer, y eso no toca tu precio ni tu
        plan.{' '}
        {/* ⚠️ Decía «a la misma tarifa», y lo era mientras el pionero pagaba la
            tarifa de lista. Desde el 28-10-2026 no: el pionero paga un
            PIONERO_DESCUENTO_PCT % menos y quien no se compromete, esta tabla. La
            frase sobrevivió a la redacción nueva de la ventaja —se arregló una y no
            la otra— y durante medio día esta pantalla ofreció un descuento arriba y
            prometió abajo que daba igual comprometerse. Lo encontró el render de la
            sección, no una prueba: para texto repartido en párrafos, leerlo entero
            encuentra lo que no busca ninguna aguja. */}
        Si prefieres no comprometerte, entras como cliente normal a la tarifa de
        esta tabla. También está bien.
      </div>
    </section>
  )
}

function Ventaja({ titulo, children }) {
  return (
    <div>
      <div style={{
        fontSize: 'var(--text-base)', fontWeight: 600,
        color: 'var(--color-text)', marginBottom: 'var(--space-2)', lineHeight: 1.35,
      }}>
        {titulo}
      </div>
      <div style={{
        fontSize: 'var(--text-base)', color: 'var(--color-text-2)', lineHeight: 1.55,
      }}>
        {children}
      </div>
    </div>
  )
}
